import { useCallback, useEffect, useId, useLayoutEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import styles from './MetricTooltip.module.css'

const TOOLTIP_WIDTH = 220
const TOOLTIP_GAP = 8

function getSafeBounds() {
  const style = window.getComputedStyle(document.documentElement)
  const pixels = name => {
    const value = style.getPropertyValue(name).trim()
    return /^-?\d+(?:\.\d+)?px$/.test(value) ? Math.max(0, Number.parseFloat(value)) : 0
  }
  const viewport = window.visualViewport
  const height = pixels('--helpdesk-viewport-height') || window.innerHeight
  const visibleLeft = viewport?.offsetLeft || 0
  const visibleTop = viewport?.offsetTop || 0
  return {
    left: Math.max(pixels('--helpdesk-safe-left'), visibleLeft) + TOOLTIP_GAP,
    right: Math.min(window.innerWidth - pixels('--helpdesk-safe-right'), visibleLeft + (viewport?.width || window.innerWidth)) - TOOLTIP_GAP,
    top: Math.max(pixels('--helpdesk-safe-top'), visibleTop) + TOOLTIP_GAP,
    bottom: Math.min(height - pixels('--helpdesk-safe-bottom'), visibleTop + (viewport?.height || window.innerHeight)) - TOOLTIP_GAP,
  }
}

function placeTooltip(anchor, size, bounds) {
  const aboveSpace = anchor.top - TOOLTIP_GAP - bounds.top
  const belowSpace = bounds.bottom - anchor.bottom - TOOLTIP_GAP
  const above = aboveSpace >= size.height || (belowSpace < size.height && aboveSpace > belowSpace)
  const top = above ? anchor.top - TOOLTIP_GAP - size.height : anchor.bottom + TOOLTIP_GAP
  return {
    left: Math.max(bounds.left, Math.min(bounds.right - size.width, anchor.left + anchor.width / 2 - size.width / 2)),
    top: Math.max(bounds.top, Math.min(bounds.bottom - size.height, top)),
  }
}

const SOURCE_COLORS = {
  'API': '#22c55e',
  'Вручную': '#f59e0b',
  'Расчёт': '#6366f1',
}

export function MetricTooltip({ description, source }) {
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState(null)
  const ref = useRef(null)
  const tooltipRef = useRef(null)
  const hideTimer = useRef(null)
  const tooltipId = useId()

  const hide = useCallback(() => {
    clearTimeout(hideTimer.current)
    setVisible(false)
    setCoords(null)
  }, [])

  const show = useCallback(() => {
    clearTimeout(hideTimer.current)
    setVisible(true)
  }, [])

  const scheduleHide = () => {
    clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(hide, 150)
  }

  const updatePosition = useCallback(() => {
    const anchor = ref.current?.getBoundingClientRect()
    const tooltip = tooltipRef.current
    if (!anchor || !tooltip) return
    const bounds = getSafeBounds()
    const width = Math.min(TOOLTIP_WIDTH, Math.max(0, bounds.right - bounds.left))
    const maxHeight = Math.max(0, bounds.bottom - bounds.top)
    if (!width || !maxHeight || anchor.bottom < bounds.top || anchor.top > bounds.bottom || anchor.right < bounds.left || anchor.left > bounds.right) {
      hide()
      return
    }
    // Measure at the available width so wrapped descriptions stay inside the safe rectangle.
    tooltip.style.width = `${width}px`
    tooltip.style.maxHeight = `${maxHeight}px`
    const size = tooltip.getBoundingClientRect()
    const position = { ...placeTooltip(anchor, size, bounds), width, maxHeight }
    setCoords(previous => previous && Object.keys(position).every(key => previous[key] === position[key]) ? previous : position)
  }, [hide])

  useLayoutEffect(() => {
    if (visible) updatePosition()
  }, [visible, description, source, updatePosition])

  useEffect(() => {
    if (!visible) return undefined
    let frame = 0
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(updatePosition)
    }
    const onPointerDown = event => {
      if (!ref.current?.contains(event.target) && !tooltipRef.current?.contains(event.target)) hide()
    }
    const onKeyDown = event => { if (event.key === 'Escape') hide() }
    const viewport = window.visualViewport
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleUpdate) : null
    if (ref.current) observer?.observe(ref.current)
    if (tooltipRef.current) observer?.observe(tooltipRef.current)
    window.addEventListener('resize', scheduleUpdate)
    window.addEventListener('scroll', scheduleUpdate, true)
    window.addEventListener('helpdesk:viewport-changed', scheduleUpdate)
    viewport?.addEventListener('resize', scheduleUpdate)
    viewport?.addEventListener('scroll', scheduleUpdate)
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      cancelAnimationFrame(frame)
      observer?.disconnect()
      window.removeEventListener('resize', scheduleUpdate)
      window.removeEventListener('scroll', scheduleUpdate, true)
      window.removeEventListener('helpdesk:viewport-changed', scheduleUpdate)
      viewport?.removeEventListener('resize', scheduleUpdate)
      viewport?.removeEventListener('scroll', scheduleUpdate)
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [visible, updatePosition, hide])

  useEffect(() => () => clearTimeout(hideTimer.current), [])

  return (
    <>
    <span
      ref={ref}
      className={styles.wrapper}
      role="button"
      tabIndex={0}
      aria-label="Описание показателя"
      aria-describedby={visible ? tooltipId : undefined}
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
      onFocus={show}
      onBlur={scheduleHide}
      onClick={event => { event.stopPropagation(); show() }}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          event.stopPropagation()
          show()
        }
      }}
    >
      <span className={styles.icon}>?</span>
    </span>
      {visible && createPortal(
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          className={styles.tooltip}
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
          style={{
            position: 'fixed',
            left: coords?.left || 0,
            top: coords?.top || 0,
            width: coords?.width || TOOLTIP_WIDTH,
            maxHeight: coords?.maxHeight,
            boxSizing: 'border-box',
            overflowY: 'auto',
            pointerEvents: 'auto',
            visibility: coords ? 'visible' : 'hidden',
          }}
        >
          <p className={styles.desc}>{description}</p>
          {source && (
            <span
              className={styles.badge}
              style={{ background: SOURCE_COLORS[source] + '22', color: SOURCE_COLORS[source] }}
            >
              {source}
            </span>
          )}
        </div>,
        document.body
      )}
    </>
  )
}
